// src/lib/supabase.ts
//
// Shared Supabase client for the frontend. Uses the ANON key only —
// privileged writes go through Netlify functions with the service key.
//
// Mirrors the Genovate portal pattern at
// C:\JSchwartz-TEMP\wsl-genovate-portal\src\lib\supabase.ts so reads
// against the shared admin_and_billing_info / tickets / etc. tables
// behave the same way across all Genovate apps.
//
// RLS in our schema:
//   - `tenants` table: open SELECT for anon + authenticated; writes
//     require service role (Netlify functions, after Auth0 JWT
//     verification + role check). See
//     supabase/migrations/2026-05-24-admin-schema.sql.
//
// Auth context pattern:
//   For tables that need per-user scoping (existing Genovate portal
//   tables), use executeAuthenticatedQuery(userEmail, ...) — it sets
//   a Postgres session variable via the `set_user_context` RPC and
//   attaches `x-user-email` so RLS can scope by email. For our
//   `tenants` table reads, the plain `supabase` client is enough.

import { createClient, type SupabaseClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

let isConfigValid = false;

function isValidConfig(): boolean {
  if (!supabaseUrl || !supabaseAnonKey) return false;
  if (supabaseUrl.includes('placeholder') || supabaseAnonKey.includes('placeholder')) return false;
  if (!supabaseUrl.startsWith('https://') || !supabaseUrl.includes('.supabase.co')) return false;
  return true;
}

let _supabase: SupabaseClient;
try {
  if (isValidConfig()) {
    _supabase = createClient(supabaseUrl, supabaseAnonKey);
    isConfigValid = true;
  } else {
    console.warn('[supabase] env not configured', {
      hasUrl: !!supabaseUrl,
      hasKey: !!supabaseAnonKey,
    });
    _supabase = createClient('https://placeholder.supabase.co', 'placeholder-key');
  }
} catch (err) {
  console.error('[supabase] init failed:', err);
  _supabase = createClient('https://placeholder.supabase.co', 'placeholder-key');
}

export const supabase = _supabase;
export const supabaseConfigured = isConfigValid;

// ─── Authenticated client (per-user RLS context) ─────────────────────
//
// Mirrors the Genovate portal: build a per-user client carrying the
// x-user-email custom header so RLS policies that read
// current_setting('app.current_user_email') can scope rows. Cached per
// userEmail to keep the client count bounded.

const authenticatedClientCache = new Map<string, SupabaseClient>();

export function createAuthenticatedClient(userEmail: string): SupabaseClient {
  if (!isConfigValid || !userEmail) return _supabase;
  const cached = authenticatedClientCache.get(userEmail);
  if (cached) return cached;
  const client = createClient(supabaseUrl, supabaseAnonKey, {
    global: { headers: { 'x-user-email': userEmail } },
    db: { schema: 'public' },
  });
  authenticatedClientCache.set(userEmail, client);
  return client;
}

/**
 * Run a query as a specific user — sets the Postgres session variable via
 * the set_user_context RPC, then attaches the x-user-email header. RLS
 * policies on tables that need per-user scoping consult both.
 *
 * For `tenants` SELECTs (open RLS), use `supabase` directly — this
 * helper exists for parity with Genovate portal billing/support queries.
 */
export async function executeAuthenticatedQuery<T>(
  userEmail: string,
  queryFn: (client: SupabaseClient) => Promise<{ data: T | null; error: unknown }>
): Promise<{ data: T | null; error: unknown }> {
  if (!isConfigValid || !userEmail) {
    return { data: null, error: { message: 'Supabase not configured or no user email' } };
  }
  const { error: setError } = await _supabase.rpc('set_user_context', { p_email: userEmail });
  if (setError) {
    console.warn('[supabase] set_user_context failed; relying on header only:', setError);
  }
  const client = createAuthenticatedClient(userEmail);
  return queryFn(client);
}

export function clearAuthenticatedClientCache(userEmail?: string) {
  if (userEmail) authenticatedClientCache.delete(userEmail);
  else authenticatedClientCache.clear();
}
